import React, { useEffect, useState } from "react";
import { Bookmark, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * FilterPresets — save / load / delete named filter combos per module.
 * Presets are persisted in localStorage under `filter_presets_<moduleKey>`.
 *
 * Props:
 *   moduleKey  string   — e.g. "orders"
 *   value      object   — current filter map (from FieldFilterBar's onChange)
 *   onLoad     fn       — called with the saved filter map when the user picks a preset
 *   extra?     object   — optional additional serialisable state to snapshot alongside
 *                         the filters (e.g. revealed columns). Restored via `onLoadExtra`.
 *   onLoadExtra fn?     — receives the `extra` slice on load
 */
export default function FilterPresets({ moduleKey, value, onLoad, extra, onLoadExtra }) {
  const storageKey = `filter_presets_${moduleKey}`;
  const [presets, setPresets] = useState({});
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      setPresets(raw ? JSON.parse(raw) : {});
    } catch { setPresets({}); }
  }, [storageKey]);

  const persist = (next) => {
    setPresets(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const save = () => {
    const name = (newName || "").trim();
    if (!name) { toast.error("Name required"); return; }
    persist({ ...presets, [name]: { filters: value || {}, extra: extra || null, saved_at: new Date().toISOString() } });
    toast.success(`Preset "${name}" saved`);
    setNewName("");
  };

  const load = (name) => {
    const p = presets[name];
    if (!p) return;
    onLoad(p.filters || {});
    if (onLoadExtra && p.extra) onLoadExtra(p.extra);
    toast.success(`Loaded "${name}"`);
    setOpen(false);
  };

  const del = (name) => {
    const c = { ...presets }; delete c[name];
    persist(c);
    toast.success(`Deleted "${name}"`);
  };

  const activeCount = Object.keys(value || {}).filter((k) => {
    const v = value[k];
    if (!v) return false;
    if (typeof v === "object") return Object.values(v).some((x) => x !== "" && x !== undefined);
    return true;
  }).length;

  const names = Object.keys(presets).sort();
  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen((s) => !s)}
        className="h-8 px-2.5 text-[11.5px] font-semibold inline-flex items-center gap-1 bg-white text-slate-700 border border-slate-200 rounded-sm hover:bg-slate-50"
        data-testid={`preset-menu-${moduleKey}`}
        title="Filter presets"
      >
        <Bookmark size={13} /> Presets
        {names.length > 0 && <span className="text-[10px] font-mono text-slate-400 ml-0.5">({names.length})</span>}
      </button>
      {open && (
        <div className="absolute z-40 mt-1 right-0 w-72 bg-white border border-slate-200 rounded-sm shadow-lg" onMouseLeave={() => setOpen(false)}>
          <div className="p-2 border-b border-slate-200 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Save current filters</div>
            <div className="flex gap-1">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && save()}
                placeholder={activeCount ? `Name (${activeCount} active)` : "Add filters first"}
                disabled={!activeCount}
                data-testid={`preset-name-${moduleKey}`}
                className="flex-1 h-7 px-2 text-[12px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
              />
              <button
                onClick={save}
                disabled={!activeCount || !newName.trim()}
                className="h-7 px-2 text-[11.5px] font-semibold text-white bg-slate-900 rounded-sm hover:bg-slate-800 disabled:bg-slate-300"
                data-testid={`preset-save-${moduleKey}`}
              >
                <Save size={11} className="inline mr-0.5" /> Save
              </button>
            </div>
          </div>
          <div className="max-h-64 overflow-auto">
            {names.length === 0 ? (
              <div className="p-3 text-[11.5px] text-slate-500 text-center">No saved presets yet</div>
            ) : names.map((name) => {
              const p = presets[name];
              const count = Object.keys(p.filters || {}).length;
              return (
                <div key={name} className="flex items-center justify-between px-3 py-1.5 hover:bg-slate-50" data-testid={`preset-row-${moduleKey}-${name}`}>
                  <button
                    onClick={() => load(name)}
                    className="flex-1 text-left text-[12px] text-slate-800"
                    data-testid={`preset-load-${moduleKey}-${name}`}
                  >
                    <div className="font-semibold">{name}</div>
                    <div className="text-[10px] text-slate-400 font-mono">{count} filter{count === 1 ? "" : "s"}</div>
                  </button>
                  <button
                    onClick={() => del(name)}
                    className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-red-700 hover:bg-white rounded-sm"
                    data-testid={`preset-delete-${moduleKey}-${name}`}
                    title="Delete preset"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
